import { GRADERS } from "@/lib/catalog/graders";
import { type Facet, facet } from "@/lib/filters/facet";
import type { Kind } from "@/lib/inventory/kind";

/**
 * The Grade floors a buyer or dealer can narrow slabs to, each inclusive ("9 and up" holds a 9 and
 * a 10). Floors rather than exact grades, because people ask for "nines and better" far more than
 * for exactly 9.5. A slab with no numeric Grade (Authentic) is under every floor.
 */
export const GRADE_FLOORS = [
  { value: "10", label: "10" },
  { value: "9.5", label: "9.5 and up" },
  { value: "9", label: "9 and up" },
  { value: "8", label: "8 and up" },
  { value: "7", label: "7 and up" },
] as const;

export type GradeFloorKey = (typeof GRADE_FLOORS)[number]["value"];

/**
 * Which facets a Kind can carry, for every filtered list (inventory and both storefronts).
 * Condition is raw-only and Grader and Grade belong to a slab (`docs/context/06-grading.md`), so
 * Graded hides Condition and only Graded offers the slab pair; Sealed carries neither. With no Kind
 * picked, Condition stays: it is how people have always narrowed to Near Mint, and it selects only
 * raw copies either way.
 */
export function kindFacets(kind: Kind | null): { condition: boolean; slab: boolean; } {
  return { condition: kind === null || kind === "raw", slab: kind === "graded" };
}

/** The number in a grade, with any designation after it ("10 Pristine") set aside; NaN if none. */
export function gradeNumber(grade: string | null | undefined): number {
  const text = grade?.trim() ?? "";
  return text ? Number(text.split(" ")[0]) : Number.NaN;
}

/** Whether a slab's grade meets a floor; a non-numeric grade (Authentic) meets none. */
export function meetsGradeFloor(grade: string | null | undefined, floor: string): boolean {
  const n = gradeNumber(grade);
  return Number.isFinite(n) && n >= Number(floor);
}

/** Narrows a picked option back to its vocabulary; a value outside it reads as no choice. */
function oneOf<T extends string>(vocabulary: readonly T[], value: string | null): T | null {
  return vocabulary.find((v) => v === value) ?? null;
}

/**
 * The slab pair, Grader then Grade floor, as facets for a list whose Kind is Graded (`kindFacets`).
 * Each list supplies only how a pick becomes its own patch, so inventory and the storefronts offer
 * the same graders, the same floors and the same wording.
 */
export function slabFacets<P>(
  { grader, gradeMin, pickGrader, pickGrade }: {
    grader: string | null;
    gradeMin: string | null;
    pickGrader: (grader: (typeof GRADERS)[number] | null) => P;
    pickGrade: (gradeMin: GradeFloorKey | null) => P;
  },
): Facet<P>[] {
  return [
    facet<P>({
      key: "grader",
      label: "Grader",
      options: GRADERS.map((g) => ({ value: g, label: g })),
      value: grader,
      restingValue: null,
      restingLabel: "Any grader",
      pick: (next) => pickGrader(oneOf(GRADERS, next)),
    }),
    facet<P>({
      key: "grade",
      label: "Grade",
      options: [...GRADE_FLOORS],
      value: gradeMin,
      restingValue: null,
      restingLabel: "Any grade",
      pick: (next) => pickGrade(oneOf(GRADE_FLOORS.map((g) => g.value), next)),
    }),
  ];
}
